/**
 * Supabase Realtime fallback transport — serverless delivery route for hosted
 * PWAs (Vercel static + Supabase Realtime). Carries the same signed transport
 * envelopes as relays; content remains E2EE (transport never sees plaintext).
 * Phoenix wire protocol, no supabase-js dependency.
 */

export interface SupabaseRealtimeConfig {
  url: string;
  anonKey: string;
}

const HEARTBEAT_INTERVAL_MS = 25_000;

/* ---------------------------- frame encoding ------------------------------ */

export type ServerFrame =
  | { kind: "reply"; ref: string; status: string; topic: string }
  | { kind: "broadcast"; topic: string; event: string; payload: unknown }
  | { kind: "heartbeat"; ref: string }
  | { kind: "close"; topic: string }
  | { kind: "unknown" };

export function encodeJoin(topic: string, ref: string): string {
  return JSON.stringify([
    "phx_join",
    {
      topic,
      event: "phx_join",
      ref,
      payload: {
        config: {
          broadcast: { self: false },
          presence: { key: "" },
          // Public channel — last-resort delivery; RLS still scopes veto:p:* topics.
          private: false,
        },
      },
    },
  ]);
}

export function encodeBroadcast(topic: string, payload: string, ref: string): string {
  return JSON.stringify(["broadcast", { topic, event: "veto-msg", payload, ref }]);
}

/** Match relay-dev MAX_EVENT_BYTES — last-resort store must not become a blob dump. */
export const MAX_SUPABASE_ENVELOPE_BYTES = 64 * 1024;

export type VetoEnvelopeRow = {
  event_id: string;
  recipient_hint: string;
  envelope: unknown;
};

/** Parse a signed EVENT JSON into a REST row, or null if it is not storeable. */
export function envelopeRowForStore(hint16: string, envelopeJson: string): VetoEnvelopeRow | null {
  const recipient_hint = hint16.toLowerCase();
  if (!/^[0-9a-f]{8,16}$/.test(recipient_hint)) return null;
  if (envelopeJson.length > MAX_SUPABASE_ENVELOPE_BYTES) return null;
  try {
    const envelope = JSON.parse(envelopeJson) as { id?: unknown };
    if (!envelope || typeof envelope !== "object" || typeof envelope.id !== "string") return null;
    if (envelope.id.length < 8 || envelope.id.length > 128) return null;
    return { event_id: envelope.id, recipient_hint, envelope };
  } catch {
    return null;
  }
}

function restHeaders(anonKey: string): HeadersInit {
  return {
    apikey: anonKey,
    Authorization: `Bearer ${anonKey}`,
    "Content-Type": "application/json",
  };
}

async function timedFetch(url: string, init: RequestInit, ms = 4_000): Promise<Response | null> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } catch {
    return null;
  } finally {
    clearTimeout(t);
  }
}

export function encodeHeartbeat(ref: string): string {
  return JSON.stringify(["heartbeat", { topic: "phoenix", ref }]);
}

export function decodeServerFrame(raw: string): ServerFrame {
  try {
    const data = JSON.parse(raw);
    if (!Array.isArray(data) || data.length < 1) return { kind: "unknown" };
    const [type, body] = data as [string, Record<string, unknown> | undefined];
    if (type === "phx_reply" && body) {
      return {
        kind: "reply",
        ref: String(body.ref ?? ""),
        topic: String(body.topic ?? ""),
        status: String((body.payload as { status?: string } | undefined)?.status ?? ""),
      };
    }
    if (type === "broadcast" && body) {
      return {
        kind: "broadcast",
        topic: String(body.topic ?? ""),
        event: String(body.event ?? ""),
        payload: body.payload,
      };
    }
    if (type === "heartbeat") return { kind: "heartbeat", ref: String(body?.ref ?? "") };
    if (type === "phx_close") return { kind: "close", topic: String(body?.topic ?? "") };
    return { kind: "unknown" };
  } catch {
    return { kind: "unknown" };
  }
}

/** Delivery channel for a recipient's 16-hex `#p` hint — both sides derive it identically. */
export function supabaseChannelForHint(hint16: string): string {
  return `realtime:veto:p:${hint16.toLowerCase()}`;
}

function realtimeWsUrl(cfg: SupabaseRealtimeConfig): string {
  const ws = cfg.url.replace(/^http/i, "ws");
  return `${ws}/realtime/v1/websocket?apikey=${encodeURIComponent(cfg.anonKey)}&vsn=1.0.0`;
}

/* -------------------------------- transport ------------------------------- */

type MessageHandler = (payload: string) => void;

/**
 * One realtime connection; subscribe() joins per-recipient topics, publish()
 * broadcasts into one. Reconnect is driven by the app's network tick.
 */
export class SupabaseRealtimeTransport {
  private ws: WebSocket | null = null;
  private handlers = new Set<MessageHandler>();
  private topics = new Set<string>();
  private ref = 0;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private connecting: Promise<void> | null = null;
  state: "idle" | "connecting" | "open" | "closed" = "idle";

  constructor(private readonly cfg: SupabaseRealtimeConfig) {}

  isUsable(): boolean {
    return !!this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  async connect(): Promise<void> {
    if (this.isUsable()) return;
    if (this.connecting) return this.connecting;
    this.connecting = new Promise<void>((resolve) => {
      try {
        const ws = new WebSocket(realtimeWsUrl(this.cfg));
        this.ws = ws;
        this.state = "connecting";
        const done = () => {
          if (this.ws === ws) this.connecting = null;
          resolve();
        };
        const failTimer = setTimeout(done, 5_000);
        ws.onopen = () => {
          clearTimeout(failTimer);
          this.state = "open";
          // Heartbeat keeps the socket alive through idle periods.
          this.heartbeat = setInterval(() => {
            try {
              ws.send(encodeHeartbeat(String(++this.ref)));
            } catch {
              /* ignore */
            }
          }, HEARTBEAT_INTERVAL_MS);
          for (const topic of this.topics) ws.send(encodeJoin(topic, String(++this.ref)));
          done();
        };
        ws.onmessage = (m) => {
          const frame = decodeServerFrame(String(m.data));
          if (frame.kind === "broadcast" && frame.event === "veto-msg") {
            const payload = typeof frame.payload === "string" ? frame.payload : JSON.stringify(frame.payload);
            for (const h of this.handlers) h(payload);
          }
        };
        ws.onclose = () => {
          clearTimeout(failTimer);
          if (this.heartbeat) clearInterval(this.heartbeat);
          this.heartbeat = null;
          if (this.ws === ws) {
            this.ws = null;
            this.state = "closed";
          }
          done();
        };
        ws.onerror = () => {
          clearTimeout(failTimer);
          done();
        };
      } catch {
        this.state = "closed";
        this.connecting = null;
        resolve();
      }
    });
    return this.connecting;
  }

  /** Subscribe to recipient topics and deliver incoming envelopes to handlers. */
  subscribe(topics: string[], onMessage: MessageHandler): () => void {
    this.handlers.add(onMessage);
    for (const t of topics) this.topics.add(t);
    if (this.isUsable()) {
      for (const t of topics) this.ws!.send(encodeJoin(t, String(++this.ref)));
    }
    return () => this.handlers.delete(onMessage);
  }

  /** Persist a signed envelope for offline catch-up (ciphertext only). */
  async persistEnvelope(hint16: string, envelopeJson: string): Promise<boolean> {
    const row = envelopeRowForStore(hint16, envelopeJson);
    if (!row) return false;
    const res = await timedFetch(`${this.cfg.url.replace(/\/+$/, "")}/rest/v1/veto_envelopes`, {
      method: "POST",
      headers: {
        ...restHeaders(this.cfg.anonKey),
        // Plain insert: a duplicate comes back as 409 (treated as stored below). No ON CONFLICT,
        // so the hardened schema needs no SELECT privilege for writes.
        Prefer: "return=minimal",
      },
      body: JSON.stringify(row),
    });
    return !!res && (res.ok || res.status === 409);
  }

  /**
   * Fetch queued envelopes for a 16-hex recipient hint (offline catch-up),
   * newest first, through the `veto_fetch_envelopes` RPC — the hardened schema
   * allows no bulk table reads. Falls back to the legacy query when the
   * migration has not been applied yet (RPC → 404).
   */
  async fetchEnvelopes(hint16: string): Promise<string[]> {
    const hint = hint16.toLowerCase();
    if (!/^[0-9a-f]{8,16}$/.test(hint)) return [];
    const base = this.cfg.url.replace(/\/+$/, "");
    let rows: unknown = null;
    const rpc = await timedFetch(`${base}/rest/v1/rpc/veto_fetch_envelopes`, {
      method: "POST",
      headers: restHeaders(this.cfg.anonKey),
      body: JSON.stringify({ hint, max_rows: 300 }),
    });
    if (rpc?.ok) {
      rows = await rpc.json().catch(() => null);
    } else if (rpc?.status === 404) {
      const legacy = await timedFetch(
        `${base}/rest/v1/veto_envelopes?recipient_hint=eq.${encodeURIComponent(hint)}&select=envelope&order=created_at.desc&limit=300`,
        { headers: restHeaders(this.cfg.anonKey) },
      );
      const raw = legacy?.ok ? ((await legacy.json().catch(() => null)) as { envelope?: unknown }[] | null) : null;
      rows = Array.isArray(raw) ? raw.map((r) => r.envelope) : null;
    }
    if (!Array.isArray(rows)) return [];
    return rows.map((r) => {
      // Tolerate PostgREST wrapping set-returning scalars as { veto_fetch_envelopes: … }.
      const v =
        r && typeof r === "object" && !Array.isArray(r) && "veto_fetch_envelopes" in r
          ? (r as { veto_fetch_envelopes: unknown }).veto_fetch_envelopes
          : r;
      return typeof v === "string" ? v : JSON.stringify(v ?? null);
    });
  }

  /** Broadcast + persist a signed envelope; true if either path accepted it. */
  async publish(topic: string, payload: string): Promise<boolean> {
    const hint = topic.split(":").pop() ?? "";
    const stored = await this.persistEnvelope(hint, payload);
    await this.connect();
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return stored;
    try {
      ws.send(encodeBroadcast(topic, payload, String(++this.ref)));
      return true;
    } catch {
      return stored;
    }
  }

  close(): void {
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
    this.handlers.clear();
    this.topics.clear();
    try {
      this.ws?.close();
    } catch {
      /* ignore */
    }
    this.ws = null;
    this.state = "closed";
  }
}
